/**
 * Datele de contact ale clientului, retinute pe dispozitiv dupa prima comanda.
 *
 * Platforma nu are conturi (checkout ca oaspete), dar a doua comanda nu are de
 * ce sa ceara din nou numele, telefonul si e-mailul: Baymard masoara formularul
 * mediu de finalizare la ~15 campuri fata de 7–8 necesare, iar campurile pe care
 * clientul le-a completat deja sunt primele de scos. Se scriu la plasarea
 * comenzii, se citesc la urmatoarea finalizare si se pot sterge dintr-o
 * apasare.
 *
 * NU se retin: adresa (are lista ei), facturarea pe firma (rara, si datele
 * fiscale nu se prepopuleaza tacit), bifele de consimtamant (fiecare comanda e
 * un consimtamant nou).
 */

export interface CustomerProfile {
  name: string;
  phone: string;
  email: string;
}

const STORAGE_PREFIX = "deliveryapp.customer.v1";

export function customerProfileStorageKey(marketKey: string): string {
  return `${STORAGE_PREFIX}.${marketKey}`;
}

export function parseCustomerProfile(raw: string | null): CustomerProfile | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const source = parsed as Record<string, unknown>;
    const name = typeof source.name === "string" ? source.name.trim() : "";
    const phone = typeof source.phone === "string" ? source.phone.trim() : "";
    const email = typeof source.email === "string" ? source.email.trim() : "";
    // Fara telefon nu e un profil: e singura identitate a clientului pe platforma.
    if (phone.length === 0) return null;
    return { name, phone, email };
  } catch {
    return null;
  }
}

export function readCustomerProfile(marketKey: string): CustomerProfile | null {
  try {
    return parseCustomerProfile(window.localStorage.getItem(customerProfileStorageKey(marketKey)));
  } catch {
    return null;
  }
}

export function writeCustomerProfile(marketKey: string, profile: CustomerProfile): void {
  try {
    window.localStorage.setItem(customerProfileStorageKey(marketKey), JSON.stringify(profile));
  } catch {
    // Fara stocare, datele se cer din nou data viitoare. Nu e o eroare.
  }
}

export function clearCustomerProfile(marketKey: string): void {
  try {
    window.localStorage.removeItem(customerProfileStorageKey(marketKey));
  } catch {
    // Idem.
  }
}

/** Cifrele numarului, pentru validarea din formular. */
export function phoneDigits(value: string): number {
  return value.replace(/\D/g, "").length;
}

/**
 * Formatul acceptat de core (`normalizePhone`): cifre, optional `+` in fata,
 * 8–20 cifre, dupa scoaterea spatiilor, punctelor, cratimelor si parantezelor.
 * Se verifica AICI, cu aceeasi regula, ca formularul sa refuze inainte de a
 * trimite ce ar refuza serverul.
 */
export function isAcceptablePhone(value: string): boolean {
  const compact = value.replace(/[\s.\-()]/g, "");
  return /^\+?[0-9]{8,20}$/.test(compact);
}

export function isPlausibleEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
}
